import { useEffect, useRef, useState } from "react";
import {
  DyteClock,
  DyteDialogManager,
  DyteParticipantsAudio,
} from "@dytesdk/react-ui-kit";
import { DyteProvider, useDyteClient } from "@dytesdk/react-web-core";
import { defaultConfig, Transcript } from "@dytesdk/ui-kit";
import { DyteParticipant } from "@dytesdk/web-core";
import { meetingApi } from "@vincer/services/api/meetingApi";
import { Mic, MicOff, Target, Users, Video, VideoOff } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { CustomStates } from "@/types/dyte";
import { setupMeetingConfigs } from "@/lib/utils";
import { useLiveMeetingAnalysis } from "@/hooks/useMeetingAnalysis";
import {
  useMeetingTranscriptById,
  useUpsertMeetingTranscript,
} from "@/hooks/useMeetingTranscript";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import AlertSection from "./components/AlertSection";
import MenteeVideoTile from "./components/MenteeVideoTile";
import MentorVideoTile from "./components/MentorVideoTile";
import SuggestionsSection from "./components/SuggestionsSection";
import TopicsSection from "./components/TopicsSection";
import VideoHeader from "./components/VideoHeader";
import VideoLoading from "./components/VideoLoading";

interface TopicEntry {
  topic: string;
  timestamp: string;
  firstMentioned: string;
}

export default function VideoConference() {
  const { mutate: upsertTranscript } = useUpsertMeetingTranscript();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId") || "";
  const authToken = searchParams.get("authToken");

  if (!sessionId) {
    throw new Error("Session ID is required");
  }

  const { data: meetingTranscript } = useMeetingTranscriptById(sessionId);
  const { data: meetingAnalysis } = useLiveMeetingAnalysis(sessionId);
  const [meeting, initMeeting] = useDyteClient();
  const [config, setConfig] = useState(defaultConfig);
  const [states, setStates] = useState<CustomStates>({
    meeting: "setup",
    sidebar: "chat",
  });

  const [isMicOn, setIsMicOn] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [remoteParticipant, setRemoteParticipant] =
    useState<DyteParticipant | null>(null);
  const transcriptListenerSet = useRef<boolean>(false);
  const handleTranscriptRef = useRef<any>(null);
  const [topicEntries, setTopicEntries] = useState<TopicEntry[]>([]);
  const previousTopicsRef = useRef<Set<string>>(new Set());
  const meetingStartTimeRef = useRef<Date | null>(null);

  const speakerMap = new Map<string, string>();
  speakerMap.set(meeting?.self.name || "", "MENTOR");

  // Set meeting start time when meeting is initialized
  useEffect(() => {
    if (meeting && !meetingStartTimeRef.current) {
      meetingStartTimeRef.current = new Date();
    }
  }, [meeting]);

  // Process topics from meeting analysis
  useEffect(() => {
    if (meetingAnalysis?.content?.key_topics) {
      const currentTopics = new Set(meetingAnalysis.content.key_topics);
      const newTopics = meetingAnalysis.content.key_topics.filter(
        (topic: string) => !previousTopicsRef.current.has(topic)
      );

      if (newTopics.length > 0) {
        const currentTime = new Date().toISOString();
        const newEntries = newTopics.map((topic: string) => ({
          topic,
          timestamp: currentTime,
          firstMentioned: currentTime,
        }));

        setTopicEntries((prevEntries) => {
          let updatedEntries = [...prevEntries, ...newEntries];
          updatedEntries = updatedEntries.sort(
            (a, b) =>
              new Date(a.firstMentioned).getTime() -
              new Date(b.firstMentioned).getTime()
          );

          updatedEntries = updatedEntries.reverse();

          return updatedEntries;
        });

        // Update the set of seen topics
        newTopics.forEach((topic: string) =>
          previousTopicsRef.current.add(topic)
        );
      }
    }
  }, [meetingAnalysis?.content?.key_topics]);

  useEffect(() => {
    if (meeting) {
      Object.assign(window, {
        meeting,
      });
      setupMeetingConfigs(meeting, states, setStates, setConfig);
    }
  }, [meeting]);

  useEffect(() => {
    async function initalizeMeeting() {
      if (!authToken) {
        alert(
          "An authToken wasn't passed, please pass an authToken in the URL query to join a meeting."
        );
        return;
      }

      const meeting = await initMeeting({
        authToken,
        defaults: {
          audio: false,
          video: false,
        },
      });

      await meeting!.joinRoom();
    }

    if (!meeting) {
      initalizeMeeting();
    }
  }, [meeting]);

  useEffect(() => {
    if (!meeting) return;
    const activeParticipants = meeting.participants.active.toArray();
    const initialRemoteParticipant = activeParticipants[0];
    if (initialRemoteParticipant) {
      setRemoteParticipant(initialRemoteParticipant);
      speakerMap.set(initialRemoteParticipant.name, "MENTEE");
    }

    const handleParticipantJoined = (participant: DyteParticipant) => {
      setRemoteParticipant(participant);
      speakerMap.set(participant.name, "MENTEE");
    };

    const handleParticipantLeft = () => {
      setRemoteParticipant(null);
    };

    meeting.participants.joined.on(
      "participantJoined",
      handleParticipantJoined
    );
    meeting.participants.active.on("participantLeft", handleParticipantLeft);

    return () => {
      meeting.participants.active.removeListener(
        "participantJoined",
        handleParticipantJoined
      );
      meeting.participants.active.removeListener(
        "participantLeft",
        handleParticipantLeft
      );

      meeting.leaveRoom();
      meetingApi.setInactive(sessionId);
    };
  }, [meeting]);

  useEffect(() => {
    // Cleanup on unmount only
    return () => {
      if (
        transcriptListenerSet.current &&
        meeting &&
        handleTranscriptRef.current
      ) {
        console.log(
          "removeListener transcript (unmount)",
          meetingTranscript,
          meeting
        );
        meeting.ai.removeListener("transcript", handleTranscriptRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!transcriptListenerSet.current && meeting && meetingTranscript) {
      const handleTranscript = async (transcript: Transcript) => {
        // @ts-ignore
        if (!transcript.isPartialTranscript) {
          console.log(
            "Session ID: ",
            sessionId,
            "Transcript: ",
            transcript.transcript,
            transcript.name
          );

          meetingTranscript.content.push({
            transcript: transcript.transcript,
            speaker: speakerMap.get(transcript.name) || "MENTOR",
            timestamp: transcript.date.toISOString(),
          });

          upsertTranscript(meetingTranscript);
        }
      };
      handleTranscriptRef.current = handleTranscript;
      meeting.ai.on("transcript", handleTranscript);
      transcriptListenerSet.current = true;
      console.log("set up transcript listener");
    }
  }, [meeting, meetingTranscript]);

  return (
    <DyteProvider value={meeting} fallback={<VideoLoading />}>
      <div className="flex h-screen flex-col overflow-hidden bg-gray-900 text-white">
        {/* Header Controls */}
        <VideoHeader sessionId={sessionId} />

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Conversation Topics */}
          <div className="w-64 overflow-y-auto border-r border-gray-700 bg-gray-800 p-4">
            <div className="mb-4">
              <TopicsSection
                topicEntries={topicEntries}
                meetingStartTime={meetingStartTimeRef.current}
              />
            </div>

            {/* Quick Actions */}
            <div className="mt-6">
              <h4 className="mb-3 text-sm font-semibold text-gray-200">
                Quick Actions
              </h4>
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-gray-300 hover:bg-gray-700"
                >
                  <Target className="mr-2 size-3" />
                  Set Goal
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-gray-300 hover:bg-gray-700"
                >
                  <Users className="mr-2 size-3" />
                  Share Resource
                </Button>
              </div>
            </div>
          </div>

          {/* Main Video Area */}
          <div className="flex flex-1 flex-col">
            {/* Critical Alerts */}
            <AlertSection alert={meetingAnalysis?.content?.alert || ""} />

            {/* Mentee Video Feed */}
            <div className="relative flex flex-1 items-center justify-center bg-gray-900 p-4">
              <div className="relative size-full overflow-hidden rounded-lg bg-gray-800">
                {/* Video Placeholder */}
                <MenteeVideoTile remoteParticipant={remoteParticipant} />

                {/* Video Controls Overlay */}
                <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-gray-900/80 px-4 py-2 backdrop-blur-sm">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!meeting) return;
                      if (isMicOn) {
                        await meeting.self.disableAudio();
                        setIsMicOn(false);
                      } else {
                        await meeting.self.enableAudio();
                        setIsMicOn(true);
                      }
                    }}
                    className={`rounded-full p-2 ${isMicOn ? "bg-gray-700 hover:bg-gray-600" : "bg-red-600 hover:bg-red-700"}`}
                  >
                    {isMicOn ? (
                      <Mic className="size-4" />
                    ) : (
                      <MicOff className="size-4" />
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!meeting) return;
                      if (isVideoOn) {
                        await meeting.self.disableVideo();
                        setIsVideoOn(false);
                      } else {
                        await meeting.self.enableVideo();
                        setIsVideoOn(true);
                      }
                    }}
                    className={`rounded-full p-2 ${isVideoOn ? "bg-gray-700 hover:bg-gray-600" : "bg-red-600 hover:bg-red-700"}`}
                  >
                    {isVideoOn ? (
                      <Video className="size-4" />
                    ) : (
                      <VideoOff className="size-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Coaching Suggestions & Mentor View */}
          <div className="w-80 space-y-4 overflow-y-auto border-l border-gray-700 bg-gray-800 p-4">
            {/* Mentor Thumbnail */}
            <Card className="border-gray-600 bg-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm text-gray-200">
                  <Video className="size-4" />
                  Your Video Feed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <MentorVideoTile isVideoOn={isVideoOn} isMicOn={isMicOn} />
              </CardContent>
            </Card>

            {/* Coaching Suggestions */}
            <SuggestionsSection
              suggestions={meetingAnalysis?.content?.mentor_suggestions || []}
            />

            {/* Quick Stats */}
            <Card className="border-gray-600 bg-gray-700">
              <CardHeader>
                <CardTitle className="text-sm text-gray-200">
                  Session Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Duration</span>
                  <DyteClock
                    meeting={meeting}
                    className="text-sm font-medium text-gray-200"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Topics Covered</span>
                  <span className="text-sm font-medium text-gray-200">
                    {topicEntries.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Engagement</span>
                  <span className="text-sm font-medium text-green-400">
                    High
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <DyteParticipantsAudio meeting={meeting} />
      <DyteDialogManager meeting={meeting} config={config} states={states} />
    </DyteProvider>
  );
}
